import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, Plus, ChevronLeft, Loader2, Trash2, Pencil, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import SwipeableBookmarkItem from './SwipeableBookmarkItem';
import { API_URL as API } from '@/utils/apiConfig';
import { toast } from 'sonner';
import axios from 'axios';

const LABEL_COLORS = [
  '#ef4444', '#3b82f6', '#22c55e', '#a855f7',
  '#f97316', '#eab308', '#ec4899', '#14b8a6',
  '#6366f1', '#f43f5e',
];

// ── Color Dot ───────────────────────────────────────────────────────────────
function ColorDot({ color, selected, onClick, size = 'md' }) {
  const dim = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  return (
    <button
      onClick={onClick}
      className={`${dim} rounded-full transition-all shrink-0 ${selected ? 'ring-2 ring-offset-1 ring-white/50 scale-110' : 'hover:scale-110'}`}
      style={{ background: color }}
      data-testid={`color-${color.replace('#', '')}`}
    />
  );
}

// ── Context Menu (right-click desktop / long-press mobile) ──────────────────
function ContextMenu({ x, y, items, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="fixed z-[200] min-w-[160px] rounded-xl shadow-xl py-1.5 animate-in fade-in zoom-in-95 duration-150"
      style={{
        top: Math.min(y, window.innerHeight - 150),
        left: Math.min(x, window.innerWidth - 180),
        background: 'var(--theme-surface)',
        border: '1px solid var(--glass-border)',
      }}
      data-testid="label-context-menu"
    >
      {items.map((item, i) => (
        <button
          key={i}
          onClick={() => { item.action(); onClose(); }}
          className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm transition-colors hover:bg-[var(--theme-hover)]"
          style={{ color: item.destructive ? '#ef4444' : 'var(--theme-text)' }}
          data-testid={`ctx-label-${item.label.toLowerCase().replace(/\s/g, '-')}`}
        >
          {item.icon}
          {item.label}
        </button>
      ))}
    </div>
  );
}

// ── Label Manager Modal (assign labels to a bookmark) ───────────────────────
export function LabelAssignModal({ open, bookmarkId, currentLabelIds = [], allLabels, onClose, onUpdated }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(new Set(currentLabelIds));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelected(new Set(currentLabelIds));
  }, [currentLabelIds, open]);

  if (!open) return null;

  const toggleLabel = (labelId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(labelId)) {
        next.delete(labelId);
      } else {
        if (next.size >= 5) {
          toast.error(t('bookmarks.max_labels'));
          return prev;
        }
        next.add(labelId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/bookmarks/${bookmarkId}/labels`, {
        label_ids: Array.from(selected),
      }, { withCredentials: true });
      onUpdated(res.data.label_ids);
      toast.success(t('bookmarks.labels_updated'));
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('bookmarks.failed_update_labels'));
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative w-full sm:max-w-sm mx-auto rounded-t-2xl sm:rounded-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200"
        style={{ background: 'var(--theme-surface)', border: '1px solid var(--glass-border)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="label-assign-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>{t('bookmarks.assign_labels')}</h3>
          <span className="text-[10px]" style={{ color: 'var(--theme-textSecondary)' }}>{selected.size}/5</span>
        </div>
        <div
          className="overflow-y-auto p-3 space-y-1"
          style={{ maxHeight: 'min(50vh, calc(var(--visual-vh, 100vh) - 200px))' }}
        >
          {allLabels.length === 0 ? (
            <p className="text-xs text-center py-4" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('bookmarks.no_labels_hint')}
            </p>
          ) : (
            allLabels.map((lbl) => {
              const isSelected = selected.has(lbl.label_id);
              return (
                <button
                  key={lbl.label_id}
                  onClick={() => toggleLabel(lbl.label_id)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors"
                  style={{
                    background: isSelected ? `${lbl.color}18` : 'transparent',
                    border: `1px solid ${isSelected ? lbl.color : 'transparent'}`,
                  }}
                  data-testid={`label-option-${lbl.label_id}`}
                >
                  <div
                    className="h-3 w-3 rounded-full shrink-0"
                    style={{ background: lbl.color }}
                  />
                  <span className="text-sm flex-1 text-left" style={{ color: 'var(--theme-text)' }}>{lbl.name}</span>
                  {isSelected && <Check className="h-3.5 w-3.5" style={{ color: lbl.color }} />}
                </button>
              );
            })
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: 'var(--glass-border)' }}>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-xs h-8 rounded-full">{t('bookmarks.cancel')}</Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="text-xs h-8 rounded-full px-4"
            style={{ background: 'var(--theme-accent)', color: '#fff' }}
            data-testid="save-labels-btn"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : t('bookmarks.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main BookmarkLabels Component ───────────────────────────────────────────
export default function BookmarkLabels({ onLoadConversation }) {
  const { t } = useTranslation();
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(LABEL_COLORS[0]);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [activeLabel, setActiveLabel] = useState(null);
  const [labelBookmarks, setLabelBookmarks] = useState([]);
  const [loadingBookmarks, setLoadingBookmarks] = useState(false);
  const [editingLabel, setEditingLabel] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const longPressTimer = useRef(null);

  const fetchLabels = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bookmark-labels`, { withCredentials: true });
      setLabels(res.data.labels || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchLabels(); }, [fetchLabels]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/bookmark-labels`, { name, color: newColor }, { withCredentials: true });
      setLabels((prev) => [...prev, { ...res.data, bookmark_count: 0 }]);
      setNewName('');
      setShowColorPicker(false);
      toast.success(t('bookmarks.label_created'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('bookmarks.failed_create_label'));
    }
    setCreating(false);
  };

  const handleUpdate = async (labelId) => {
    const updates = {};
    if (editName.trim()) updates.name = editName.trim();
    if (editColor) updates.color = editColor;
    if (!updates.name && !updates.color) { setEditingLabel(null); return; }

    try {
      await axios.put(`${API}/bookmark-labels/${labelId}`, updates, { withCredentials: true });
      setLabels((prev) => prev.map((l) =>
        l.label_id === labelId ? { ...l, ...updates } : l
      ));
      if (activeLabel?.label_id === labelId) {
        setActiveLabel((prev) => ({ ...prev, ...updates }));
      }
      toast.success(t('bookmarks.label_updated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('bookmarks.failed_update'));
    }
    setEditingLabel(null);
  };

  const handleDelete = async (labelId) => {
    try {
      await axios.delete(`${API}/bookmark-labels/${labelId}`, { withCredentials: true });
      setLabels((prev) => prev.filter((l) => l.label_id !== labelId));
      if (activeLabel?.label_id === labelId) setActiveLabel(null);
      toast.success(t('bookmarks.label_deleted'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('bookmarks.failed_delete'));
    }
    setDeleteTarget(null);
  };

  const openLabel = async (label) => {
    setActiveLabel(label);
    setLoadingBookmarks(true);
    try {
      const res = await axios.get(`${API}/bookmark-labels/${label.label_id}/bookmarks`, { withCredentials: true });
      setLabelBookmarks(res.data.bookmarks || []);
    } catch {
      setLabelBookmarks([]);
    }
    setLoadingBookmarks(false);
  };

  const handleDeleteBookmark = async (bookmarkId) => {
    try {
      await axios.delete(`${API}/bookmarks/${bookmarkId}`, { withCredentials: true });
      setLabelBookmarks((prev) => prev.filter((b) => b.bookmark_id !== bookmarkId));
      toast.success(t('bookmarks.bookmark_removed'));
    } catch { toast.error(t('bookmarks.failed_remove')); }
  };

  const handleRemoveLabelFromBookmark = async (bookmarkId) => {
    if (!activeLabel) return;
    try {
      await axios.delete(`${API}/bookmarks/${bookmarkId}/labels/${activeLabel.label_id}`, { withCredentials: true });
      setLabelBookmarks((prev) => prev.filter((b) => b.bookmark_id !== bookmarkId));
      toast.success(t('bookmarks.label_removed'));
    } catch { toast.error(t('bookmarks.failed_remove_label')); }
  };

  // Long-press for mobile
  const startLongPress = (e, label) => {
    longPressTimer.current = setTimeout(() => {
      const touch = e.touches?.[0];
      const x = touch?.clientX || e.clientX || 100;
      const y = touch?.clientY || e.clientY || 100;
      showContextMenuFor(x, y, label);
    }, 500);
  };

  const cancelLongPress = () => { clearTimeout(longPressTimer.current); };

  // Right-click for desktop
  const handleContextMenu = (e, label) => {
    e.preventDefault();
    showContextMenuFor(e.clientX, e.clientY, label);
  };

  const showContextMenuFor = (x, y, label) => {
    setContextMenu({
      x, y, label,
      items: [
        {
          label: t('bookmarks.edit'), icon: <Pencil className="h-3.5 w-3.5" />,
          action: () => { setEditingLabel(label.label_id); setEditName(label.name); setEditColor(label.color); },
        },
        {
          label: t('bookmarks.delete'), icon: <Trash2 className="h-3.5 w-3.5" />,
          action: () => setDeleteTarget(label), destructive: true,
        },
      ],
    });
  };

  // ── Inside a label view ──
  if (activeLabel) {
    return (
      <div className="flex flex-col flex-1 min-h-0" data-testid="label-detail-view">
        <div className="flex items-center gap-2 px-3 pt-2 pb-2 shrink-0">
          <button
            onClick={() => { setActiveLabel(null); fetchLabels(); }}
            className="p-1 rounded-md hover:bg-[var(--theme-hover)] transition"
            data-testid="back-to-labels"
          >
            <ChevronLeft className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
          <div className="h-3 w-3 rounded-full shrink-0" style={{ background: activeLabel.color }} />
          <span className="text-xs font-semibold truncate" style={{ color: 'var(--theme-text)' }}>
            {activeLabel.name}
          </span>
          <span className="text-[10px] ml-auto" style={{ color: 'var(--theme-textSecondary)' }}>
            {labelBookmarks.length} {t('bookmarks.items_count', { count: labelBookmarks.length }).split(' ').slice(-1)[0]}
          </span>
        </div>

        <ScrollArea className="flex-1 min-h-0">
          <div className="p-2">
            {loadingBookmarks ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : labelBookmarks.length === 0 ? (
              <div className="px-2 py-8 text-center">
                <Tag className="h-8 w-8 mx-auto mb-2" style={{ color: activeLabel.color, opacity: 0.4 }} />
                <p className="text-sm" style={{ color: 'var(--theme-textSecondary)', opacity: 0.6 }}>{t('bookmarks.no_bookmarks_with_label')}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }}>
                  {t('bookmarks.add_label_hint')}
                </p>
              </div>
            ) : (
              labelBookmarks.map((bm) => (
                <div key={bm.bookmark_id} className="relative group/bm">
                  <SwipeableBookmarkItem
                    bookmark={bm}
                    onSelect={(convId, msgId) => onLoadConversation(convId, false, msgId)}
                    onDelete={handleRemoveLabelFromBookmark}
                  />
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>
    );
  }

  // ── Labels list view ──
  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="bookmark-labels-view">
      {/* Create new label */}
      <div className="px-3 pt-2 pb-1 shrink-0 space-y-1.5">
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="h-5 w-5 rounded-full shrink-0 ring-1 ring-white/20"
            style={{ background: newColor }}
            data-testid="new-label-color-btn"
          />
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            placeholder={t('bookmarks.new_label_name')}
            className="flex-1 bg-transparent text-xs outline-none py-1 placeholder:text-muted-foreground/50"
            style={{ color: 'var(--theme-text)' }}
            maxLength={30}
            data-testid="new-label-input"
          />
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6 shrink-0"
            onClick={handleCreate}
            disabled={creating || !newName.trim()}
            data-testid="create-label-btn"
          >
            {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3.5 w-3.5" style={{ color: 'var(--theme-accent)' }} />}
          </Button>
        </div>
        {showColorPicker && (
          <div className="flex flex-wrap gap-1.5 py-1 animate-in fade-in duration-150" data-testid="color-picker">
            {LABEL_COLORS.map((c) => (
              <ColorDot key={c} color={c} selected={newColor === c} onClick={() => setNewColor(c)} size="sm" />
            ))}
          </div>
        )}
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <div className="p-2">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : labels.length === 0 ? (
            <div className="px-2 py-8 text-center">
              <Tag className="h-8 w-8 mx-auto mb-2" style={{ color: 'var(--theme-textSecondary)', opacity: 0.3 }} />
              <p className="text-sm" style={{ color: 'var(--theme-textSecondary)', opacity: 0.6 }}>{t('bookmarks.no_labels_yet')}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }}>
                {t('bookmarks.create_labels_hint')}
              </p>
            </div>
          ) : (
            labels.map((lbl) => (
              <div
                key={lbl.label_id}
                className="mb-1 rounded-xl border border-transparent hover:border-border bg-card hover:bg-muted/30 transition-colors cursor-pointer select-none"
                onClick={() => editingLabel !== lbl.label_id && openLabel(lbl)}
                onContextMenu={(e) => handleContextMenu(e, lbl)}
                onTouchStart={(e) => startLongPress(e, lbl)}
                onTouchEnd={cancelLongPress}
                onTouchMove={cancelLongPress}
                data-testid={`label-item-${lbl.label_id}`}
              >
                {editingLabel === lbl.label_id ? (
                  <div className="px-3 py-2.5 space-y-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <div
                        className="h-3.5 w-3.5 rounded-full shrink-0"
                        style={{ background: editColor || lbl.color }}
                      />
                      <input
                        autoFocus
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="flex-1 bg-transparent text-sm font-medium outline-none border-b-2 py-0.5"
                        style={{ color: 'var(--theme-text)', borderColor: editColor || lbl.color }}
                        maxLength={30}
                        data-testid="edit-label-name-input"
                      />
                      <button
                        onClick={() => handleUpdate(lbl.label_id)}
                        className="p-1 rounded-md hover:bg-[var(--theme-hover)]"
                        data-testid="save-edit-label"
                      >
                        <Check className="h-3.5 w-3.5" style={{ color: 'var(--theme-accent)' }} />
                      </button>
                      <button
                        onClick={() => setEditingLabel(null)}
                        className="p-1 rounded-md hover:bg-[var(--theme-hover)]"
                      >
                        <X className="h-3.5 w-3.5" style={{ color: 'var(--theme-textSecondary)' }} />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {LABEL_COLORS.map((c) => (
                        <ColorDot key={c} color={c} selected={editColor === c} onClick={() => setEditColor(c)} size="sm" />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 px-3 py-2.5">
                    <div
                      className="h-3.5 w-3.5 rounded-full shrink-0"
                      style={{ background: lbl.color }}
                    />
                    <span className="text-sm font-medium truncate flex-1" style={{ color: 'var(--theme-text)' }}>
                      {lbl.name}
                    </span>
                    <span className="text-[10px] shrink-0" style={{ color: 'var(--theme-textSecondary)' }}>
                      {lbl.bookmark_count || 0}
                    </span>
                    <ChevronLeft className="h-3 w-3 shrink-0 rotate-180" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }} />
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bookmarks.delete_label_title', { name: deleteTarget?.name })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('bookmarks.delete_label_desc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('bookmarks.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteTarget.label_id)} className="bg-red-600 hover:bg-red-700">
              {t('bookmarks.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
